import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Droplet, Heart, AlertCircle, AlertTriangle, Loader2, Activity,
} from "lucide-react";

const SPECIALTY_META = {
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
};

/**
 * WeightProtocolBanner — Phase 3b.
 * Fetches active specialties for the current patient (via /platform/command-
 * center) and shows the relevant Weight & Fluid clinical protocols. Renders
 * one card per specialty that has data. If a patient has both nephro AND
 * cardio, they see both protocols stacked.
 */
export default function WeightProtocolBanner() {
  const [specialties, setSpecialties] = useState([]);
  const [protocols, setProtocols] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/command-center");
        const active = (data?.tiles || []).map((t) => t.specialty);
        // Only nephrology + cardiology have weight/fluid protocols.
        const supported = active.filter((s) => ["nephrology", "cardiology"].includes(s));
        setSpecialties(supported);
        const results = {};
        for (const s of supported) {
          try {
            const r = await api.get(`/platform/weight-protocol/${s}`);
            results[s] = r.data.protocol;
          } catch { /* skip 404s silently */ }
        }
        setProtocols(results);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]"
           data-testid="weight-protocol-loading">
        <Loader2 size={14} className="animate-spin" /> Loading clinical protocols…
      </div>
    );
  }
  if (specialties.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="weight-protocol-banner">
      {specialties.map((s) => {
        const proto = protocols[s];
        if (!proto) return null;
        const meta = SPECIALTY_META[s];
        const Icon = meta.icon;
        return (
          <div key={s}
               data-testid={`weight-protocol-${s}`}
               className="ros-card"
               style={{ borderLeftWidth: 2, borderLeftColor: meta.accent,
                        background: `${meta.accent}08` }}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                   style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}>
                <Icon size={16} style={{ color: meta.accent }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                        style={{ color: meta.accent, background: `${meta.accent}22`,
                                 border: `1px solid ${meta.accent}55` }}>
                    {meta.label} protocol
                  </span>
                </div>
                <div className="font-heading text-lg text-white mt-1.5">{proto.pattern}</div>
                <div className="text-xs text-[#A3ACA7] mt-1 leading-relaxed">{proto.description}</div>

                {/* Thresholds */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  {proto.thresholds.map((t, i) => {
                    const isRed = t.label.toLowerCase().includes("red");
                    const col = isRed ? "#F87171" : "#FBBF24";
                    const IconT = isRed ? AlertCircle : AlertTriangle;
                    return (
                      <div key={i} className="rounded-lg p-2.5"
                           data-testid={`weight-threshold-${s}-${i}`}
                           style={{ background: `${col}0d`, border: `1px solid ${col}33` }}>
                        <div className="flex items-center gap-1.5">
                          <IconT size={12} style={{ color: col }} />
                          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: col }}>
                            {t.label}
                          </span>
                        </div>
                        <div className="text-xs text-white mt-1 font-medium">{t.rule}</div>
                        <div className="text-[11px] text-[#A3ACA7] mt-1 leading-snug">{t.action}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Fluid target + sodium */}
                <div className="flex flex-wrap gap-2 mt-3" data-testid={`weight-targets-${s}`}>
                  {proto.fluid_target?.value_ml && (
                    <span className="text-[11px] px-2 py-1 rounded-full border"
                          style={{ color: meta.accent, borderColor: `${meta.accent}55`,
                                   background: `${meta.accent}12` }}>
                      Fluid limit: {proto.fluid_target.value_ml} mL/day
                    </span>
                  )}
                  {proto.sodium_target_mg && (
                    <span className="text-[11px] px-2 py-1 rounded-full border border-[#272E2B] text-[#A3ACA7]">
                      Sodium: &lt;{proto.sodium_target_mg} mg/day
                    </span>
                  )}
                </div>
                {proto.fluid_target?.note && (
                  <div className="text-[11px] text-[#A3ACA7] mt-1.5 leading-snug italic">
                    {proto.fluid_target.note}
                  </div>
                )}

                {/* Red flags */}
                {proto.red_flags?.length > 0 && (
                  <details className="mt-3" data-testid={`weight-redflags-${s}`}>
                    <summary className="text-[11px] uppercase tracking-wider text-[#F87171] cursor-pointer flex items-center gap-1.5">
                      <Activity size={11} /> When to escalate ({proto.red_flags.length})
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {proto.red_flags.map((rf, i) => (
                        <li key={i} className="text-xs text-[#D1D5DB] flex items-start gap-2 leading-relaxed">
                          <span className="text-[#F87171] font-bold">!</span>
                          <span>{rf}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="text-[10px] text-[#7A8480] mt-3 uppercase tracking-wider">
                  Source: {proto.source}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
